import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function PartnersPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '合作伙伴 - 智研云 | 携手共赢 | 生态合作',
    description: '智研云与众多知名企业建立战略合作关系，共同为客户提供优质的研发费用管理服务。我们期待与更多合作伙伴携手共赢，共建研发管理生态圈。',
    keywords: '合作伙伴,战略合作,生态合作,携手共赢,合作企业',
    canonical: '/partners',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '合作伙伴 - 智研云',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/partners`,
      description: '智研云与众多知名企业建立战略合作关系',
      mainEntity: {
        '@type': 'Organization',
        name: '智研云',
        url: import.meta.env.VITE_SITE_URL || 'https://example.com',
        description: '专业的研发费用管理系统'
      }
    }
  });

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    partnerType: '',
    companySize: '',
    businessScope: '',
    message: '',
    intention: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ipn8aeh8g2mrj4ark0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          phone: '',
          partnerType: '',
          companySize: '',
          businessScope: '',
          message: '',
          intention: ''
        });
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnerCategories = [
    {
      title: '战略合作伙伴',
      description: '与行业领军企业深度合作，共同推动研发管理数字化',
      partners: [
        { name: '华为云', logo: 'ri-cloud-line' },
        { name: '阿里云', logo: 'ri-cloud-line' },
        { name: '腾讯云', logo: 'ri-cloud-line' },
        { name: '金蝶', logo: 'ri-file-chart-line' }
      ]
    },
    {
      title: '财税服务机构',
      description: '携手专业财税机构，为客户提供一站式服务',
      partners: [
        { name: '德勤', logo: 'ri-briefcase-line' },
        { name: '普华永道', logo: 'ri-briefcase-line' },
        { name: '毕马威', logo: 'ri-briefcase-line' },
        { name: '安永', logo: 'ri-briefcase-line' }
      ]
    },
    {
      title: '技术合作伙伴',
      description: '与优秀技术服务商合作，持续提升产品能力',
      partners: [
        { name: '微软', logo: 'ri-windows-line' },
        { name: 'AWS', logo: 'ri-server-line' },
        { name: 'Oracle', logo: 'ri-database-line' },
        { name: 'SAP', logo: 'ri-settings-line' }
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-hand-heart-line',
      title: '共同成长',
      description: '与合作伙伴携手发展，实现互利共赢'
    },
    {
      icon: 'ri-customer-service-line',
      title: '专业支持',
      description: '提供全方位的技术和市场支持'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '丰厚回报',
      description: '具有竞争力的合作分成机制'
    },
    {
      icon: 'ri-team-line',
      title: '资源共享',
      description: '共享客户资源和行业经验'
    }
  ];

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-cyan-50 via-blue-50 to-white py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Business%20partnership%20and%20collaboration%20concept%20with%20handshake%20and%20teamwork%20elements%20in%20soft%20cyan%20and%20blue%20gradient%20professional%20modern%20design&width=1920&height=600&seq=partners-hero-bg&orientation=landscape')] opacity-5 bg-cover bg-center"></div>
          
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-links-line"></i>
                <span>携手共赢</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                我们的<br />
                <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">合作伙伴网络</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                与行业领先企业和专业机构深度合作<br />
                共同为客户提供更优质的服务
              </p>

              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-cyan-600 text-xl"></i>
                  <span>50+战略合作伙伴</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-cyan-600 text-xl"></i>
                  <span>覆盖全国主要城市</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-checkbox-circle-fill text-cyan-600 text-xl"></i>
                  <span>服务10000+企业</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partner Categories */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="space-y-20">
              {partnerCategories.map((category, index) => (
                <div key={index}>
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">{category.title}</h2>
                    <p className="text-lg text-gray-600">{category.description}</p>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {category.partners.map((partner, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-2xl p-8 border-2 border-gray-100 hover:border-cyan-300 transition-all hover:shadow-xl hover:-translate-y-2 group cursor-pointer"
                      >
                        <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl mb-6 mx-auto group-hover:scale-110 transition-transform">
                          <i className={`${partner.logo} text-4xl text-white`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 text-center group-hover:text-cyan-600 transition-colors">
                          {partner.name}
                        </h3>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                成为合作伙伴的<span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">核心优势</span>
              </h2>
              <p className="text-xl text-gray-600">
                我们为合作伙伴提供全方位的支持和保障
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border border-gray-100"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl mb-6">
                    <i className={`${benefit.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Partner Types */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                多种<span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">合作模式</span>
              </h2>
              <p className="text-xl text-gray-600">
                根据您的业务特点，选择最适合的合作方式
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-store-line',
                  title: '渠道代理商',
                  description: '销售我们的产品和服务，获得丰厚的销售佣金',
                  features: ['销售返佣', '市场支持', '培训赋能']
                },
                {
                  icon: 'ri-service-line',
                  title: '服务合作伙伴',
                  description: '为客户提供实施、培训等增值服务',
                  features: ['服务收益', '技术支持', '认证体系']
                },
                {
                  icon: 'ri-code-box-line',
                  title: '技术集成商',
                  description: '将我们的产品集成到您的解决方案中',
                  features: ['API对接', '联合开发', '技术共享']
                }
              ].map((type, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-cyan-50 rounded-2xl p-8 border-2 border-cyan-200 hover:border-cyan-400 transition-all hover:shadow-xl hover:-translate-y-2"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl mb-6">
                    <i className={`${type.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {type.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {type.description}
                  </p>
                  <div className="space-y-3">
                    {type.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <i className="ri-check-line text-cyan-600 text-xl"></i>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-cyan-600 to-blue-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Business%20partnership%20success%20with%20growth%20and%20collaboration%20elements%20in%20white%20and%20light%20blue%20modern%20professional%20background&width=1920&height=400&seq=partners-cta-bg&orientation=landscape')] opacity-10 bg-cover bg-center"></div>
          
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center relative z-10">
            <h2 className="text-4xl font-bold text-white mb-6">
              成为我们的合作伙伴
            </h2>
            <p className="text-xl text-white/90 mb-8">
              携手共创美好未来，实现互利共赢
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button 
                onClick={() => setShowModal(true)}
                className="bg-white text-cyan-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                申请成为合作伙伴
              </button>
              <button className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer">
                下载合作手册
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Partner Application Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-cyan-600 to-blue-600 text-white p-6 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">申请成为合作伙伴</h3>
                  <p className="text-white/90">填写以下信息，我们将尽快与您联系</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form id="partner-application-form" data-readdy-form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-6">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    公司名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                    placeholder="请输入公司全称"
                  />
                </div>

                {/* Contact Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    联系人姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                    placeholder="请输入您的姓名"
                  />
                </div>

                {/* Contact Phone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    联系电话 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors text-sm"
                    placeholder="请输入手机号码"
                  />
                </div>

                {/* Cooperation Intention */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    合作意向 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="intention"
                    value={formData.intention}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:outline-none transition-colors text-sm resize-none"
                    placeholder="请简要描述您的合作意向和期望..."
                  />
                  <div className="text-right text-xs text-gray-400 mt-1">
                    {formData.intention.length}/500
                  </div>
                </div>

                {/* Submit Status */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
                    <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                    <div>
                      <p className="font-semibold text-green-800">提交成功！</p>
                      <p className="text-sm text-green-700">我们将在1-2个工作日内与您联系</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center gap-3">
                    <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
                    <div>
                      <p className="font-semibold text-red-800">提交失败</p>
                      <p className="text-sm text-red-700">请检查网络连接后重试</p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      '提交申请'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
